import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "../services/api";
import BillingInterestForm from "./BillingInterestForm";
import useI18n from "../i18n/useI18n";

const THEME_KEY = "hookora_theme";

const PLAN_PRICING = {
  creator: {
    monthly: 5999,
    yearly: 59990,
    yearlyMonthlyEquivalent: 4999,
  },
  pro: {
    monthly: 11999,
    yearly: 119990,
    yearlyMonthlyEquivalent: 9999,
  },
};

const walletPresetAmounts = [2000, 5000, 10000, 20000];

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `₦${amount.toLocaleString()}`;
};

const formatLedgerAmount = (value) => {
  const amount = Number(value || 0);
  const sign = amount > 0 ? "+" : "";
  return `${sign}${formatCurrency(amount)}`;
};

const PricingPlans = ({ heading }) => {
  const { t } = useI18n();

  const tt = useCallback(
    (key, fallback) => {
      const value = t(key);
      return value === key ? fallback : value;
    },
    [t]
  );

  const resolvedHeading = heading || tt("pricing.heading", "Plans built for creators");

  const PLAN_TOOL_SUMMARY = useMemo(
    () => ({
      free: [
        tt("tools.hooks", "Hooks"),
        tt("tools.titles", "Titles"),
        tt("tools.captions", "Captions"),
        tt("tools.hashtags", "Hashtags"),
        tt("tools.descriptions", "Descriptions"),
        tt("tools.scripts", "Scripts"),
      ],
      creator: [
        tt("tools.hookAngles", "Hook Angles"),
        tt("tools.ctas", "CTAs"),
        tt("tools.thumbnailAnalyzer", "Thumbnail Analyzer"),
        tt("tools.aiHumanizer", "AI Humanizer"),
        tt("tools.repurposer", "Repurposer"),
        tt("tools.adCopy", "Ad Copy"),
        tt("tools.carousels", "Carousels"),
        tt("tools.viralRewrite", "Viral Rewrite"),
        tt("tools.offerPositioning", "Offer Positioning"),
        tt("tools.hookScore", "Hook Score"),
        tt("tools.nigerianRewrite", "Nigerian Rewrite"),
        tt("tools.retentionIntro", "Retention Intro"),
        tt("tools.commentIdeas", "Comment Ideas"),
      ],
      pro: [
        tt("tools.trendConverter", "Trend Converter"),
        tt("tools.brandVoiceTrainer", "Brand Voice Trainer"),
        tt("tools.contentDifferentiation", "Content Differentiation"),
      ],
    }),
    [tt]
  );

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [currentPlan, setCurrentPlan] = useState("free");
  const [profile, setProfile] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [loadingPlan, setLoadingPlan] = useState("");
  const [theme, setTheme] = useState(getStoredTheme());
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [rewards, setRewards] = useState(null);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const [previewMap, setPreviewMap] = useState({});
  const [walletLedger, setWalletLedger] = useState([]);
  const [walletAmount, setWalletAmount] = useState("5000");
  const [walletFundingLoading, setWalletFundingLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");

  const plans = useMemo(
    () => [
      {
        key: "free",
        name: tt("plans.free", "Free"),
        price: "₦0",
        period: tt("pricing.periodMonth", "/month"),
        subPrice: tt("pricing.startWithCoreWorkflow", "Start with the core workflow"),
        description: tt(
          "pricing.freeDescription",
          "Ideal for testing Hookora and getting familiar with the core workflow before unlocking more power."
        ),
        features: [
          tt("pricing.features.free.generations", "Monthly generations included"),
          tt("pricing.features.free.savedHooks", "Saved hooks included"),
          tt("pricing.features.free.coreToolsOnly", "Core tools only"),
          tt(
            "pricing.features.free.coreToolsList",
            "Hooks, titles, captions, hashtags, descriptions, and scripts"
          ),
          tt("pricing.features.free.simpleWorkflow", "Simple workflow for everyday creation"),
          tt("pricing.features.free.oneUser", "Single-user access"),
        ],
        unlockTitle: tt("pricing.coreWritingTools", "Core writing tools"),
        unlockItems: PLAN_TOOL_SUMMARY.free,
        accent: "#6b7280",
        bg: "#ffffff",
      },
      {
        key: "creator",
        name: tt("plans.creator", "Creator"),
        price:
          billingCycle === "yearly"
            ? formatCurrency(PLAN_PRICING.creator.yearly)
            : formatCurrency(PLAN_PRICING.creator.monthly),
        period:
          billingCycle === "yearly"
            ? tt("pricing.periodYear", "/year")
            : tt("pricing.periodMonth", "/month"),
        subPrice:
          billingCycle === "yearly"
            ? `${tt("pricing.approx", "Approx.")} ${formatCurrency(
                PLAN_PRICING.creator.yearlyMonthlyEquivalent
              )}${tt("pricing.periodMonth", "/month")} • ${tt(
                "pricing.twoMonthsFree",
                "2 months free"
              )}`
            : tt("pricing.billedMonthly", "Billed monthly"),
        description: tt(
          "pricing.creatorDescription",
          "Ideal for individual creators who want premium tools for packaging, rewrites, retention, and stronger content quality."
        ),
        features: [
          tt("pricing.features.creator.generations", "More monthly generations"),
          tt("pricing.features.creator.savedHooks", "More saved hooks"),
          tt("pricing.features.creator.everythingInFree", "Everything in Free"),
          tt("pricing.features.creator.premiumTools", "Premium workflow tools"),
          tt(
            "pricing.features.creator.workflowSupport",
            "Better support for packaging, rewrites, and retention"
          ),
          tt("pricing.features.creator.analytics", "Richer creator workflow support"),
        ],
        unlockTitle: tt("pricing.premiumWorkflowTools", "Premium workflow tools"),
        unlockItems: PLAN_TOOL_SUMMARY.creator,
        accent: "#2563eb",
        bg: "#eff6ff",
        popular: true,
      },
      {
        key: "pro",
        name: tt("plans.pro", "Pro"),
        price:
          billingCycle === "yearly"
            ? formatCurrency(PLAN_PRICING.pro.yearly)
            : formatCurrency(PLAN_PRICING.pro.monthly),
        period:
          billingCycle === "yearly"
            ? tt("pricing.periodYear", "/year")
            : tt("pricing.periodMonth", "/month"),
        subPrice:
          billingCycle === "yearly"
            ? `${tt("pricing.approx", "Approx.")} ${formatCurrency(
                PLAN_PRICING.pro.yearlyMonthlyEquivalent
              )}${tt("pricing.periodMonth", "/month")} • ${tt(
                "pricing.twoMonthsFree",
                "2 months free"
              )}`
            : tt("pricing.billedMonthly", "Billed monthly"),
        description: tt(
          "pricing.proDescription",
          "Ideal for serious creators and small teams looking for deeper strategy tools, more volume, and stronger creative control."
        ),
        features: [
          tt("pricing.features.pro.generations", "Highest monthly generations"),
          tt("pricing.features.pro.everythingInCreator", "Everything in Creator"),
          tt("pricing.features.pro.advancedTools", "Advanced strategic tools"),
          tt(
            "pricing.features.pro.strategySupport",
            "Stronger strategic creator support"
          ),
          tt(
            "pricing.features.pro.futurePresets",
            "Priority access to deeper workflow expansion"
          ),
          tt("pricing.features.pro.prioritySupport", "Priority support"),
        ],
        unlockTitle: tt("pricing.advancedStrategicTools", "Advanced strategic tools"),
        unlockItems: PLAN_TOOL_SUMMARY.pro,
        accent: "#7c3aed",
        bg: "#f5f3ff",
      },
    ],
    [billingCycle, tt, PLAN_TOOL_SUMMARY]
  );

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 12px 30px rgba(15, 23, 42, 0.06)",
      softShadow: isDark
        ? "0 10px 26px rgba(2, 6, 23, 0.34)"
        : "0 8px 24px rgba(15, 23, 42, 0.04)",
      successBg: isDark ? "rgba(8,47,73,0.28)" : "#ecfeff",
      successBorder: isDark ? "rgba(34,211,238,0.32)" : "#a5f3fc",
      successText: isDark ? "#a5f3fc" : "#155e75",
      featureBg: isDark ? "rgba(30,41,59,0.72)" : "#ffffff",
      featureBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      buttonCurrentBg: isDark ? "#334155" : "#d1d5db",
      buttonCurrentText: isDark ? "#e2e8f0" : "#374151",
      billingCardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      rewardsBg: isDark ? "rgba(16,24,40,0.82)" : "#ffffff",
      rewardsAccentBg: isDark ? "rgba(37,99,235,0.16)" : "#eff6ff",
      rewardsAccentBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      rewardsAccentText: isDark ? "#bfdbfe" : "#1d4ed8",
      walletBg: isDark ? "rgba(88,28,135,0.14)" : "#faf5ff",
      walletBorder: isDark ? "rgba(192,132,252,0.2)" : "#e9d5ff",
      walletText: isDark ? "#e9d5ff" : "#7c3aed",
      subtlePanelBg: isDark ? "rgba(15,23,42,0.56)" : "#f8fafc",
      subtlePanelBorder: isDark ? "rgba(148,163,184,0.14)" : "#e5e7eb",
      infoBg: isDark ? "rgba(15,23,42,0.76)" : "#ffffff",
      infoBorder: isDark ? "rgba(96,165,250,0.18)" : "#dbeafe",
      warningBg: isDark ? "rgba(120,53,15,0.28)" : "#fff7ed",
      warningBorder: isDark ? "rgba(251,191,36,0.22)" : "#fdba74",
      warningText: isDark ? "#fde68a" : "#9a3412",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      positiveText: isDark ? "#86efac" : "#166534",
      negativeText: isDark ? "#fca5a5" : "#b91c1c",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      cycleBg: isDark ? "rgba(15,23,42,0.9)" : "#f8fafc",
      cycleActiveBg: "#2563eb",
      cycleActiveText: "#ffffff",
      cycleInactiveText: isDark ? "#cbd5e1" : "#374151",
      creatorPanelBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      creatorPanelBorder: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
      creatorPanelText: isDark ? "#bfdbfe" : "#1d4ed8",
      proPanelBg: isDark ? "rgba(109,40,217,0.14)" : "#f5f3ff",
      proPanelBorder: isDark ? "rgba(216,180,254,0.22)" : "#d8b4fe",
      proPanelText: isDark ? "#e9d5ff" : "#7c3aed",
      freePanelBg: isDark ? "rgba(120,53,15,0.14)" : "#fff7ed",
      freePanelBorder: isDark ? "rgba(251,191,36,0.22)" : "#fdba74",
      freePanelText: isDark ? "#fde68a" : "#9a3412",
    }),
    [isDark]
  );

  const planStyles = useMemo(
    () => ({
      free: {
        bg: isDark ? "rgba(30,41,59,0.78)" : "#ffffff",
        accent: "#94a3b8",
      },
      creator: {
        bg: isDark ? "rgba(30,58,138,0.18)" : "#eff6ff",
        accent: "#2563eb",
      },
      pro: {
        bg: isDark ? "rgba(88,28,135,0.18)" : "#f5f3ff",
        accent: "#7c3aed",
      },
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    background: colors.inputBg,
    color: colors.inputText,
    boxSizing: "border-box",
    outline: "none",
    fontSize: "14px",
  };

  const queryMessage = useMemo(() => {
    if (typeof window === "undefined") return "";

    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const wallet = params.get("wallet");
    const provider = params.get("provider");

    if (checkout === "success") {
      return provider === "paystack"
        ? tt(
            "pricing.messages.paymentSubmittedPaystack",
            "Your payment was submitted successfully. Your plan should update shortly."
          )
        : tt(
            "pricing.messages.paymentReceived",
            "Payment received. Your plan should update shortly."
          );
    }

    if (wallet === "success") {
      return provider === "paystack"
        ? tt(
            "pricing.messages.walletSubmittedPaystack",
            "Your wallet funding payment was submitted successfully."
          )
        : tt(
            "pricing.messages.walletReceived",
            "Wallet funding received successfully."
          );
    }

    if (checkout === "cancelled") {
      return tt("pricing.messages.checkoutCancelled", "Checkout was cancelled.");
    }

    return "";
  }, [tt]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const syncStoredUser = (userData) => {
    try {
      const existingUser = localStorage.getItem("user");
      const parsedUser = existingUser ? JSON.parse(existingUser) : {};

      localStorage.setItem(
        "user",
        JSON.stringify({
          ...parsedUser,
          ...userData,
        })
      );

      window.dispatchEvent(new CustomEvent("hookora-auth-updated"));
    } catch (err) {
      console.error("Failed to sync local user state:", err);
    }
  };

  const loadRewards = async () => {
    try {
      setLoadingRewards(true);
      const response = await API.get("/referrals/me");
      setRewards(response.data);
    } catch (err) {
      console.error("Failed to load rewards summary:", err);
      setRewards(null);
    } finally {
      setLoadingRewards(false);
    }
  };

  const loadWalletLedger = async () => {
    try {
      const response = await API.get("/billing/wallet/ledger");
      setWalletLedger(response.data || []);
    } catch (err) {
      console.error("Failed to load wallet ledger:", err);
      setWalletLedger([]);
    }
  };

  const loadPreviews = async (selectedInterval = billingCycle) => {
    try {
      const paidPlans = plans.filter((plan) => plan.key !== "free");
      const results = await Promise.all(
        paidPlans.map(async (plan) => {
          try {
            const response = await API.post("/billing/checkout-preview", {
              plan: plan.key,
              interval: selectedInterval,
            });
            return [plan.key, response.data];
          } catch (err) {
            console.error(`Failed to load checkout preview for ${plan.key}:`, err);
            return [plan.key, null];
          }
        })
      );

      setPreviewMap(Object.fromEntries(results));
    } catch (err) {
      console.error("Failed to load checkout previews:", err);
    }
  };

  const loadProfile = async () => {
    try {
      const [profileRes, billingRes] = await Promise.all([
        API.get("/users/profile"),
        API.get("/billing/status"),
      ]);

      const mergedProfile = {
        ...profileRes.data,
        wallet_balance_naira: billingRes?.data?.wallet_balance_naira || 0,
      };

      setProfile(mergedProfile);
      setCurrentPlan(profileRes.data.plan || "free");
      setBillingStatus(billingRes.data || null);

      syncStoredUser({
        id: profileRes.data.id,
        username: profileRes.data.username,
        email: profileRes.data.email,
        plan: profileRes.data.plan,
        is_admin: profileRes.data.is_admin,
      });

      await Promise.all([loadRewards(), loadWalletLedger()]);
    } catch (err) {
      console.error("Failed to load pricing profile:", err);

      if (err?.response?.status === 401) {
        setMessage(
          tt("pricing.messages.loginToManageBilling", "Log in to manage your billing and rewards.")
        );
      } else {
        setMessage(
          tt("pricing.messages.failedToLoadBilling", "Failed to load billing details.")
        );
      }
    }
  };

  useEffect(() => {
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadPreviews(billingCycle);
  }, [billingCycle]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!queryMessage) return undefined;

    setMessage(queryMessage);

    const refreshOne = setTimeout(() => {
      loadProfile();
    }, 2500);

    const refreshTwo = setTimeout(() => {
      loadProfile();
    }, 6000);

    return () => {
      clearTimeout(refreshOne);
      clearTimeout(refreshTwo);
    };
  }, [queryMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyText = async (value, fieldName) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(""), 1600);
    } catch (err) {
      console.error("Failed to copy text:", err);
      setMessage(tt("pricing.messages.failedToCopy", "Failed to copy."));
    }
  };

  const handleFreePlan = async () => {
    if (currentPlan === "free") {
      setMessage(tt("pricing.messages.alreadyOnFree", "You are already on the Free plan."));
      return;
    }

    try {
      setLoadingPlan("free");
      setMessage("");

      const res = await API.post("/users/change-plan", { plan: "free" });

      setCurrentPlan(res.data.user.plan);
      setProfile((prev) => ({
        ...(prev || {}),
        ...res.data.user,
        usage: res.data.usage,
        cycle: res.data.cycle,
      }));
      setBillingStatus((prev) => ({
        ...(prev || {}),
        plan: "free",
        billing_interval: "monthly",
        billing_status: "inactive",
        paystack_subscription_code: null,
        paystack_plan_code: null,
        billing_current_period_end: null,
      }));

      syncStoredUser(res.data.user);

      setMessage(tt("pricing.messages.changedToFree", "Plan changed to Free successfully."));
      await Promise.all([loadRewards(), loadPreviews(billingCycle), loadWalletLedger()]);
    } catch (err) {
      console.error("Failed to change plan:", err);
      setMessage(
        err?.response?.data?.detail ||
          tt("pricing.messages.failedToChangePlan", "Failed to change plan.")
      );
    } finally {
      setLoadingPlan("");
    }
  };

  const handlePaidPlanCheckout = async (planKey) => {
    if (
      planKey === currentPlan &&
      billingStatus?.billing_status === "active" &&
      (billingStatus?.billing_interval || "monthly") === billingCycle
    ) {
      setMessage(
        tt("pricing.messages.alreadyOnPlan", `You are already on ${planKey.toUpperCase()} (${billingCycle}).`)
      );
      return;
    }

    try {
      setLoadingPlan(planKey);
      setMessage("");

      const previewResponse = await API.post("/billing/checkout-preview", {
        plan: planKey,
        interval: billingCycle,
      });
      const preview = previewResponse?.data || null;

      if (preview) {
        setPreviewMap((prev) => ({
          ...prev,
          [planKey]: preview,
        }));
      }

      const res = await API.post("/billing/checkout-session", {
        plan: planKey,
        interval: billingCycle,
      });
      const checkoutUrl = res?.data?.checkout_url;

      if (!checkoutUrl) {
        throw new Error(tt("pricing.messages.checkoutUrlMissing", "Checkout URL is missing."));
      }

      window.location.href = checkoutUrl;
    } catch (err) {
      console.error("Failed to start checkout:", err);
      setMessage(
        err?.response?.data?.detail ||
          err?.message ||
          tt("pricing.messages.failedToStartCheckout", "Failed to start checkout.")
      );
      setLoadingPlan("");
    }
  };

  const handleWalletFunding = async () => {
    const amount = Number(walletAmount || 0);

    if (!amount || amount < 100) {
      setMessage(
        tt("pricing.messages.invalidWalletAmount", "Enter a valid wallet funding amount.")
      );
      return;
    }

    try {
      setWalletFundingLoading(true);
      setMessage("");

      const response = await API.post("/billing/wallet/fund", {
        amount_naira: amount,
      });

      const checkoutUrl = response?.data?.checkout_url;
      if (!checkoutUrl) {
        throw new Error(tt("pricing.messages.walletUrlMissing", "Wallet funding URL is missing."));
      }

      window.location.href = checkoutUrl;
    } catch (err) {
      console.error("Failed to start wallet funding:", err);
      setMessage(
        err?.response?.data?.detail ||
          err?.message ||
          tt(
            "pricing.messages.failedToStartWalletFunding",
            "Failed to start wallet funding."
          )
      );
    } finally {
      setWalletFundingLoading(false);
    }
  };

  const handleChoosePlan = async (planKey) => {
    if (planKey === "free") {
      await handleFreePlan();
      return;
    }

    await handlePaidPlanCheckout(planKey);
  };

  const formatDate = (value) => {
    if (!value) return "—";

    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const formatPlanLabel = (plan) => {
    const normalized = (plan || "free").toLowerCase();
    if (normalized === "creator") return tt("plans.creatorBadge", "Creator");
    if (normalized === "pro") return tt("plans.proBadge", "Pro");
    return tt("plans.freeBadge", "Free");
  };

  const formatBillingInterval = (interval) => {
    const normalized = (interval || "monthly").toLowerCase();
    return normalized === "yearly"
      ? tt("pricing.yearly", "Yearly")
      : tt("pricing.monthly", "Monthly");
  };

  const formatLedgerType = (value) => {
    const map = {
      funding: tt("pricing.walletFunding", "Wallet funding"),
      billing_applied: tt("pricing.appliedToBilling", "Applied to billing"),
    };
    return map[value] || value?.replaceAll("_", " ") || tt("pricing.transaction", "Transaction");
  };

  const getPlanBadgeStyle = (plan) => {
    const normalized = (plan || "free").toLowerCase();

    if (normalized === "creator") {
      return {
        color: "#1d4ed8",
        background: isDark ? "rgba(29,78,216,0.18)" : "#dbeafe",
        border: `1px solid ${isDark ? "rgba(147,197,253,0.34)" : "#93c5fd"}`,
      };
    }

    if (normalized === "pro") {
      return {
        color: isDark ? "#d8b4fe" : "#6d28d9",
        background: isDark ? "rgba(109,40,217,0.18)" : "#f3e8ff",
        border: `1px solid ${isDark ? "rgba(216,180,254,0.34)" : "#d8b4fe"}`,
      };
    }

    return {
      color: isDark ? "#fbbf24" : "#92400e",
      background: isDark ? "rgba(245,158,11,0.18)" : "#fef3c7",
      border: `1px solid ${isDark ? "rgba(252,211,77,0.28)" : "#fcd34d"}`,
    };
  };

  const getButtonLabel = (plan) => {
    const isCurrent = currentPlan === plan.key;
    const isLoading = loadingPlan === plan.key;
    const isActivePaidPlan =
      isCurrent &&
      plan.key !== "free" &&
      billingStatus?.billing_status &&
      ["active", "trialing", "past_due", "unpaid"].includes(
        billingStatus.billing_status
      ) &&
      (billingStatus?.billing_interval || "monthly") === billingCycle;

    if (isLoading) {
      return plan.key === "free"
        ? tt("pricing.updating", "Updating...")
        : tt("pricing.redirecting", "Redirecting...");
    }

    if (isCurrent && plan.key === "free") {
      return tt("pricing.currentPlan", "Current plan");
    }

    if (isActivePaidPlan) {
      return tt("pricing.currentPlan", "Current plan");
    }

    if (plan.key === "free") {
      return tt("pricing.switchToFree", "Switch to Free");
    }

    return `${tt("pricing.choose", "Choose")} ${plan.name}`;
  };

  const isCurrentPlanCard = (planKey) => {
    if (planKey === "free") {
      return currentPlan === "free";
    }

    return (
      currentPlan === planKey &&
      ["active", "trialing", "past_due", "unpaid"].includes(
        billingStatus?.billing_status || ""
      ) &&
      (billingStatus?.billing_interval || "monthly") === billingCycle
    );
  };

  const getUnlockPanelStyle = (planKey) => {
    if (planKey === "creator") {
      return {
        background: colors.creatorPanelBg,
        border: colors.creatorPanelBorder,
        color: colors.creatorPanelText,
      };
    }

    if (planKey === "pro") {
      return {
        background: colors.proPanelBg,
        border: colors.proPanelBorder,
        color: colors.proPanelText,
      };
    }

    return {
      background: colors.freePanelBg,
      border: colors.freePanelBorder,
      color: colors.freePanelText,
    };
  };

  return (
    <div
      style={{
        background: colors.pageBg,
        backgroundImage: colors.pageGradient,
        minHeight: "100vh",
        padding: isMobile ? "24px 14px" : "32px 20px",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <h1
            style={{
              margin: 0,
              fontSize: isMobile ? "30px" : "36px",
              color: colors.text,
              lineHeight: "1.15",
            }}
          >
            {resolvedHeading}
          </h1>
          <p
            style={{
              color: colors.textMuted,
              marginTop: "10px",
              fontSize: isMobile ? "15px" : "16px",
              lineHeight: "1.6",
              maxWidth: "820px",
              marginInline: "auto",
            }}
          >
            {tt(
              "pricing.heroDescription",
              "Choose the Hookora plan that matches your workflow, usage level, and creator goals."
            )}
          </p>

          <div
            style={{
              marginTop: "18px",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: colors.cycleBg,
              border: `1px solid ${colors.border}`,
              borderRadius: "999px",
              padding: "6px",
              boxShadow: colors.softShadow,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              style={{
                border: "none",
                borderRadius: "999px",
                padding: "10px 16px",
                fontWeight: "700",
                cursor: "pointer",
                background:
                  billingCycle === "monthly" ? colors.cycleActiveBg : "transparent",
                color:
                  billingCycle === "monthly"
                    ? colors.cycleActiveText
                    : colors.cycleInactiveText,
              }}
            >
              {tt("pricing.monthly", "Monthly")}
            </button>

            <button
              type="button"
              onClick={() => setBillingCycle("yearly")}
              style={{
                border: "none",
                borderRadius: "999px",
                padding: "10px 16px",
                fontWeight: "700",
                cursor: "pointer",
                background:
                  billingCycle === "yearly" ? colors.cycleActiveBg : "transparent",
                color:
                  billingCycle === "yearly"
                    ? colors.cycleActiveText
                    : colors.cycleInactiveText,
              }}
            >
              {tt("pricing.yearly", "Yearly")}
            </button>

            <span
              style={{
                padding: "8px 12px",
                borderRadius: "999px",
                background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                color: isDark ? "#bfdbfe" : "#1d4ed8",
                fontWeight: "700",
                fontSize: "12px",
              }}
            >
              {tt("pricing.save17", "Save 17%")} • {tt("pricing.twoMonthsFree", "2 months free")}
            </span>
          </div>
        </div>

        {profile?.usage && (
          <div
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: "18px",
              padding: "18px",
              marginBottom: "24px",
              boxShadow: colors.softShadow,
              backdropFilter: isDark ? "blur(14px)" : "none",
            }}
          >
            <div
              style={{
                fontWeight: "700",
                color: colors.text,
                marginBottom: "8px",
              }}
            >
              {tt("pricing.currentUsage", "Current usage")}
            </div>
            <div style={{ color: colors.textSoft, fontSize: "14px", lineHeight: "1.8" }}>
              {tt("pricing.plan", "Plan")}:{" "}
              <span
                style={{
                  ...getPlanBadgeStyle(profile.plan),
                  fontSize: "12px",
                  fontWeight: "700",
                  padding: "7px 11px",
                  borderRadius: "999px",
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              >
                {formatPlanLabel(profile.plan)}
              </span>
              <br />
              {tt("pricing.billingStatus", "Billing status")}:{" "}
              <strong>{billingStatus?.billing_status || tt("pricing.inactive", "Inactive")}</strong>
              <br />
              {tt("pricing.billingInterval", "Billing interval")}:{" "}
              <strong>{formatBillingInterval(billingStatus?.billing_interval)}</strong>
              <br />
              {tt("pricing.monthlyGenerations", "Monthly generations")}:{" "}
              <strong>
                {profile.usage.monthly_generation_count} /{" "}
                {profile.usage.monthly_generation_limit}
              </strong>
              <br />
              {tt("pricing.remainingThisMonth", "Remaining this month")}:{" "}
              <strong>{profile.usage.monthly_generations_remaining}</strong>
              <br />
              {tt("pricing.savedHooks", "Saved hooks")}:{" "}
              <strong>
                {profile.usage.saved_hooks_count}
                {profile.usage.saved_hook_limit !== null
                  ? ` / ${profile.usage.saved_hook_limit}`
                  : ` / ${tt("pricing.unlimited", "Unlimited")}`}
              </strong>
              <br />
              {tt("pricing.resetIn", "Reset in")}:{" "}
              <strong>
                {profile?.cycle?.days_until_reset ?? "—"} {tt("pricing.days", "days")}
              </strong>
              <br />
              {tt("pricing.billingPeriodEnd", "Billing period end")}:{" "}
              <strong>{formatDate(billingStatus?.billing_current_period_end)}</strong>
            </div>
          </div>
        )}

        <div
          style={{
            background: colors.rewardsBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "20px",
            padding: isMobile ? "18px" : "22px",
            marginBottom: "24px",
            boxShadow: colors.softShadow,
            backdropFilter: isDark ? "blur(14px)" : "none",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "14px",
              alignItems: isMobile ? "flex-start" : "center",
              flexDirection: isMobile ? "column" : "row",
              marginBottom: "16px",
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  color: colors.text,
                  fontSize: isMobile ? "22px" : "24px",
                }}
              >
                {tt("pricing.creatorRewards", "Creator Rewards")}
              </h2>
              <p
                style={{
                  margin: "8px 0 0",
                  color: colors.textMuted,
                  lineHeight: "1.7",
                  fontSize: "14px",
                }}
              >
                {tt(
                  "pricing.creatorRewardsDescription",
                  "Share Hookora, earn reward credits, and keep your creator workflow growing."
                )}
              </p>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: colors.rewardsAccentBg,
                border: `1px solid ${colors.rewardsAccentBorder}`,
                color: colors.rewardsAccentText,
                padding: "10px 14px",
                borderRadius: "999px",
                fontWeight: "700",
                fontSize: "13px",
              }}
            >
              {tt("pricing.rewardCreditsBalance", "Reward Credits Balance")}:{" "}
              <span>{formatCurrency(rewards?.reward_credits_balance_naira || 0)}</span>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                background: colors.subtlePanelBg,
                border: `1px solid ${colors.subtlePanelBorder}`,
                borderRadius: "16px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  color: colors.text,
                  fontWeight: "700",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                {tt("pricing.shareYourReferral", "Share your referral")}
              </div>

              <div style={{ display: "grid", gap: "12px" }}>
                <div>
                  <div
                    style={{
                      color: colors.textMuted,
                      fontSize: "12px",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontWeight: "700",
                    }}
                  >
                    {tt("pricing.referralCode", "Referral code")}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        background: colors.cardBgStrong,
                        border: `1px solid ${colors.border}`,
                        borderRadius: "12px",
                        padding: "12px 14px",
                        color: colors.text,
                        fontWeight: "700",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {loadingRewards ? tt("common.loading", "Loading") : rewards?.referral_code || "—"}
                    </div>
                    <button
                      onClick={() =>
                        rewards?.referral_code && copyText(rewards.referral_code, "code")
                      }
                      disabled={!rewards?.referral_code}
                      style={{
                        padding: "12px 16px",
                        borderRadius: "12px",
                        border: "none",
                        background: "#2563eb",
                        color: "#fff",
                        fontWeight: "700",
                        cursor: rewards?.referral_code ? "pointer" : "not-allowed",
                        opacity: rewards?.referral_code ? 1 : 0.6,
                      }}
                    >
                      {copiedField === "code"
                        ? tt("common.copied", "Copied")
                        : tt("common.copyCode", "Copy code")}
                    </button>
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      color: colors.textMuted,
                      fontSize: "12px",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontWeight: "700",
                    }}
                  >
                    {tt("pricing.referralLink", "Referral link")}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        background: colors.cardBgStrong,
                        border: `1px solid ${colors.border}`,
                        borderRadius: "12px",
                        padding: "12px 14px",
                        color: colors.textSoft,
                        fontSize: "13px",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {loadingRewards ? tt("common.loading", "Loading") : rewards?.referral_link || "—"}
                    </div>
                    <button
                      onClick={() =>
                        rewards?.referral_link && copyText(rewards.referral_link, "link")
                      }
                      disabled={!rewards?.referral_link}
                      style={{
                        padding: "12px 16px",
                        borderRadius: "12px",
                        border: "none",
                        background: "#111827",
                        color: "#fff",
                        fontWeight: "700",
                        cursor: rewards?.referral_link ? "pointer" : "not-allowed",
                        opacity: rewards?.referral_link ? 1 : 0.6,
                      }}
                    >
                      {copiedField === "link"
                        ? tt("common.copied", "Copied")
                        : tt("common.copyLink", "Copy link")}
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    background: colors.warningBg,
                    border: `1px solid ${colors.warningBorder}`,
                    color: colors.warningText,
                    borderRadius: "12px",
                    padding: "12px 14px",
                    lineHeight: "1.6",
                    fontSize: "13px",
                  }}
                >
                  {tt(
                    "pricing.referralRewardNote",
                    "Reward Credits are applied before Wallet Balance, and Wallet Balance is applied before card payment during checkout."
                  )}
                </div>
              </div>
            </div>

            <div
              style={{
                background: colors.subtlePanelBg,
                border: `1px solid ${colors.subtlePanelBorder}`,
                borderRadius: "16px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  color: colors.text,
                  fontWeight: "700",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                {tt("pricing.rewardsSummary", "Rewards summary")}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    background: colors.infoBg,
                    border: `1px solid ${colors.infoBorder}`,
                    borderRadius: "14px",
                    padding: "14px",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {tt("pricing.rewardCredits", "Reward Credits")}
                  </div>
                  <div style={{ color: colors.text, fontWeight: "800", fontSize: "20px" }}>
                    {formatCurrency(rewards?.reward_credits_balance_naira || 0)}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.walletBg,
                    border: `1px solid ${colors.walletBorder}`,
                    borderRadius: "14px",
                    padding: "14px",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {tt("pricing.walletBalance", "Wallet Balance")}
                  </div>
                  <div style={{ color: colors.walletText, fontWeight: "800", fontSize: "20px" }}>
                    {formatCurrency(profile?.wallet_balance_naira || 0)}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.infoBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "14px",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {tt("pricing.totalReferrals", "Total referrals")}
                  </div>
                  <div style={{ color: colors.text, fontWeight: "700", fontSize: "18px" }}>
                    {rewards?.total_referrals ?? 0}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.infoBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "14px",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {tt("pricing.verifiedReferrals", "Verified referrals")}
                  </div>
                  <div style={{ color: colors.text, fontWeight: "700", fontSize: "18px" }}>
                    {rewards?.verified_referrals ?? 0}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.infoBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "14px",
                    gridColumn: "1 / -1",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {tt("pricing.paidReferrals", "Paid referrals")}
                  </div>
                  <div style={{ color: colors.text, fontWeight: "700", fontSize: "18px" }}>
                    {rewards?.paid_referrals ?? 0}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                background: colors.subtlePanelBg,
                border: `1px solid ${colors.subtlePanelBorder}`,
                borderRadius: "16px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  color: colors.text,
                  fontWeight: "700",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                {tt("pricing.fundWalletBalance", "Fund Wallet Balance")}
              </div>

              <div
                style={{
                  color: colors.textMuted,
                  fontSize: "13px",
                  lineHeight: "1.7",
                  marginBottom: "12px",
                }}
              >
                {tt(
                  "pricing.fundWalletDescription",
                  "Add money to your wallet so future billing can use your Wallet Balance before charging your card."
                )}
              </div>

              <div style={{ display: "grid", gap: "12px" }}>
                <input
                  type="number"
                  min="100"
                  step="100"
                  value={walletAmount}
                  onChange={(e) => setWalletAmount(e.target.value)}
                  placeholder={tt(
                    "pricing.enterWalletFundingAmount",
                    "Enter wallet funding amount"
                  )}
                  style={inputStyle}
                />

                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {walletPresetAmounts.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => setWalletAmount(String(amount))}
                      style={{
                        padding: "10px 12px",
                        borderRadius: "10px",
                        border: `1px solid ${colors.borderStrong}`,
                        background: colors.cardBgStrong,
                        color: colors.text,
                        fontWeight: "700",
                        cursor: "pointer",
                      }}
                    >
                      {formatCurrency(amount)}
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleWalletFunding}
                  disabled={walletFundingLoading}
                  style={{
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: "none",
                    background: "#7c3aed",
                    color: "#fff",
                    fontWeight: "700",
                    cursor: walletFundingLoading ? "not-allowed" : "pointer",
                    opacity: walletFundingLoading ? 0.8 : 1,
                  }}
                >
                  {walletFundingLoading
                    ? tt("pricing.redirecting", "Redirecting...")
                    : tt("pricing.fundWallet", "Fund Wallet")}
                </button>
              </div>
            </div>

            <div
              style={{
                background: colors.subtlePanelBg,
                border: `1px solid ${colors.subtlePanelBorder}`,
                borderRadius: "16px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  color: colors.text,
                  fontWeight: "700",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                {tt("pricing.walletTransactionHistory", "Wallet transaction history")}
              </div>

              {walletLedger.length ? (
                <div style={{ display: "grid", gap: "10px", maxHeight: "280px", overflowY: "auto" }}>
                  {walletLedger.slice(0, 8).map((entry) => (
                    <div
                      key={entry.id}
                      style={{
                        background: colors.cardBgStrong,
                        border: `1px solid ${colors.border}`,
                        borderRadius: "12px",
                        padding: "12px 14px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "10px",
                          flexWrap: "wrap",
                          marginBottom: "4px",
                        }}
                      >
                        <div style={{ color: colors.text, fontWeight: "700", fontSize: "14px" }}>
                          {formatLedgerType(entry.entry_type)}
                        </div>
                        <div
                          style={{
                            color:
                              Number(entry.amount_naira || 0) >= 0
                                ? colors.positiveText
                                : colors.negativeText,
                            fontWeight: "800",
                            fontSize: "14px",
                          }}
                        >
                          {formatLedgerAmount(entry.amount_naira)}
                        </div>
                      </div>

                      <div style={{ color: colors.textMuted, fontSize: "12px", lineHeight: "1.6" }}>
                        {entry.created_at ? new Date(entry.created_at).toLocaleString() : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: colors.textMuted, fontSize: "13px", lineHeight: "1.7" }}>
                  {tt("pricing.noWalletTransactions", "No wallet transactions yet.")}
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              background: colors.subtlePanelBg,
              border: `1px solid ${colors.subtlePanelBorder}`,
              borderRadius: "14px",
              padding: "14px 16px",
              color: colors.textSoft,
              lineHeight: "1.7",
              fontSize: "14px",
            }}
          >
            {tt("pricing.checkoutOrderPrefix", "At checkout, Hookora applies")}{" "}
            <strong>{tt("pricing.rewardCredits", "Reward Credits")}</strong>{" "}
            {tt("pricing.then", "then")}{" "}
            <strong>{tt("pricing.walletBalance", "Wallet Balance")}</strong>{" "}
            {tt("pricing.then", "then")}{" "}
            <strong>{tt("pricing.cardPayment", "Card Payment")}</strong>{" "}
            {tt("pricing.checkoutOrderSuffix", "to cover the remaining amount.")}
          </div>
        </div>

        {message && (
          <div
            style={{
              marginBottom: "20px",
              background: colors.successBg,
              color: colors.successText,
              border: `1px solid ${colors.successBorder}`,
              borderRadius: "14px",
              padding: "14px 16px",
            }}
          >
            {message}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "18px",
          }}
        >
          {plans.map((plan) => {
            const isCurrent = isCurrentPlanCard(plan.key);
            const styleSet = planStyles[plan.key];
            const preview = previewMap?.[plan.key] || null;
            const unlockPanel = getUnlockPanelStyle(plan.key);

            return (
              <div
                key={plan.key}
                style={{
                  position: "relative",
                  background: styleSet.bg,
                  border: isCurrent
                    ? `2px solid ${styleSet.accent}`
                    : `1px solid ${colors.border}`,
                  borderRadius: "22px",
                  padding: isMobile ? "20px" : "24px",
                  boxShadow: colors.shadow,
                  backdropFilter: isDark ? "blur(14px)" : "none",
                }}
              >
                {plan.popular && (
                  <div
                    style={{
                      position: "absolute",
                      top: "14px",
                      right: "14px",
                      background: isDark ? "#e2e8f0" : "#111827",
                      color: isDark ? "#020617" : "#fff",
                      fontSize: "12px",
                      fontWeight: "700",
                      padding: "6px 10px",
                      borderRadius: "999px",
                    }}
                  >
                    {tt("pricing.mostPopular", "Most popular")}
                  </div>
                )}

                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: styleSet.accent,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {plan.name}
                </div>

                {plan.key !== "free" && (
                  <div
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        ...getPlanBadgeStyle(plan.key),
                        fontSize: "12px",
                        fontWeight: "700",
                        padding: "7px 11px",
                        borderRadius: "999px",
                        display: "inline-block",
                      }}
                    >
                      {formatPlanLabel(plan.key)}
                    </span>

                    {billingCycle === "yearly" && (
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          padding: "7px 11px",
                          borderRadius: "999px",
                          display: "inline-block",
                          background: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
                          color: isDark ? "#bfdbfe" : "#1d4ed8",
                        }}
                      >
                        {tt("pricing.save17", "Save 17%")}
                      </span>
                    )}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "6px",
                    marginTop: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: isMobile ? "34px" : "40px",
                      fontWeight: "800",
                      color: colors.text,
                    }}
                  >
                    {plan.price}
                  </span>
                  <span style={{ color: colors.textMuted }}>{plan.period}</span>
                </div>

                {plan.subPrice && (
                  <div
                    style={{
                      marginTop: "4px",
                      color: colors.textMuted,
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    {plan.subPrice}
                  </div>
                )}

                <p
                  style={{
                    color: colors.textSoft,
                    lineHeight: "1.6",
                    minHeight: isMobile ? "unset" : "64px",
                  }}
                >
                  {plan.description}
                </p>

                <div
                  style={{
                    marginTop: "16px",
                    padding: "14px",
                    borderRadius: "16px",
                    background: unlockPanel.background,
                    border: `1px solid ${unlockPanel.border}`,
                  }}
                >
                  <div
                    style={{
                      color: unlockPanel.color,
                      fontSize: "13px",
                      fontWeight: "800",
                      marginBottom: "8px",
                    }}
                  >
                    {plan.unlockTitle}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gap: "8px",
                    }}
                  >
                    {plan.unlockItems.map((item) => (
                      <div
                        key={item}
                        style={{
                          color: unlockPanel.color,
                          fontSize: "13px",
                          lineHeight: "1.55",
                          fontWeight: "700",
                        }}
                      >
                        • {item}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: "16px", display: "grid", gap: "10px" }}>
                  {plan.features.map((feature) => (
                    <div
                      key={feature}
                      style={{
                        background: colors.featureBg,
                        border: `1px solid ${colors.featureBorder}`,
                        borderRadius: "12px",
                        padding: "10px 12px",
                        color: colors.textSoft,
                        fontSize: "14px",
                        lineHeight: "1.5",
                      }}
                    >
                      {feature}
                    </div>
                  ))}
                </div>

                {plan.key !== "free" && preview && (
                  <div
                    style={{
                      marginTop: "18px",
                      background: colors.infoBg,
                      border: `1px solid ${colors.infoBorder}`,
                      borderRadius: "14px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        color: colors.text,
                        fontWeight: "700",
                        marginBottom: "10px",
                        fontSize: "14px",
                      }}
                    >
                      {tt("pricing.billingPreview", "Billing preview")}
                    </div>

                    <div style={{ color: colors.textSoft, fontSize: "13px", lineHeight: "1.8" }}>
                      {tt("pricing.planAmount", "Plan amount")}:{" "}
                      <strong>{formatCurrency(preview.plan_amount_naira)}</strong>
                      <br />
                      {tt("pricing.billingCycle", "Billing cycle")}:{" "}
                      <strong>{formatBillingInterval(preview.interval)}</strong>
                      <br />
                      {tt("pricing.rewardCreditsApplied", "Reward Credits applied")}:{" "}
                      <strong>{formatCurrency(preview.reward_credits_applied_naira)}</strong>
                      <br />
                      {tt("pricing.walletBalanceApplied", "Wallet Balance applied")}:{" "}
                      <strong>{formatCurrency(preview.wallet_balance_applied_naira)}</strong>
                      <br />
                      {tt("pricing.cardDueNow", "Card due now")}:{" "}
                      <strong>{formatCurrency(preview.card_due_naira)}</strong>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleChoosePlan(plan.key)}
                  disabled={loadingPlan === plan.key}
                  style={{
                    marginTop: "20px",
                    width: "100%",
                    padding: "14px 16px",
                    borderRadius: "12px",
                    border: "none",
                    background: isCurrent
                      ? colors.buttonCurrentBg
                      : `linear-gradient(135deg, ${plan.accent}, ${
                          plan.key === "pro" ? "#4c1d95" : "#1d4ed8"
                        })`,
                    color: isCurrent ? colors.buttonCurrentText : "#fff",
                    fontWeight: "700",
                    cursor: loadingPlan === plan.key ? "not-allowed" : "pointer",
                    opacity: loadingPlan === plan.key ? 0.8 : 1,
                  }}
                >
                  {getButtonLabel(plan)}
                </button>

                {plan.key !== "free" && (
                  <div
                    style={{
                      marginTop: "10px",
                      color: colors.textMuted,
                      fontSize: "12px",
                      lineHeight: "1.5",
                    }}
                  >
                    {tt(
                      "pricing.secureRecurringCheckoutPrefix",
                      "Secure recurring checkout for"
                    )}{" "}
                    {billingCycle}{" "}
                    {tt("pricing.secureRecurringCheckoutSuffix", "billing.")}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <BillingInterestForm />

        <div
          style={{
            marginTop: "28px",
            background: colors.billingCardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "18px",
            padding: "20px",
            boxShadow: colors.softShadow,
            backdropFilter: isDark ? "blur(14px)" : "none",
          }}
        >
          <h3 style={{ marginTop: 0, color: colors.text }}>
            {tt("pricing.billingAndSubscriptions", "Billing and subscriptions")}
          </h3>
          <p style={{ color: colors.textSoft, lineHeight: "1.7", marginBottom: 0 }}>
            {tt(
              "pricing.billingAndSubscriptionsDescription",
              "Choose your plan, manage upgrades, and keep your billing details aligned with your Hookora usage."
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPlans;